import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, ArrowLeft } from 'lucide-react';
import { ContratoWithDetails, PropostaParaContrato } from '@/types/contrato';
import { useContratos } from '@/hooks/useContratos';
import { useContratoForm } from '@/hooks/useContratoForm';
import { useAuthorization } from '@/contexts/AuthorizationContext';
import { 
  ContratoSelecao, 
  ContratoConfirmacao, 
  ContratoFormulario, 
  ContratoVisualizacao, 
  ContratoLista 
} from '@/components/contratos';
import { ContratosService } from '@/services/contratosService';

export default function Contratos() {
  // Controle de autorização
  const { userRole } = useAuthorization();
  const isAdmin = userRole === 'admin';
  const isVendedor = userRole === 'vendedor';
  const isParceiro = userRole === 'parceiro';
  const isRestricted = isVendedor || isParceiro;

  // Estados do fluxo
  const [propostaSelecionada, setPropostaSelecionada] = useState<PropostaParaContrato | null>(null);
  const [contratoAtual, setContratoAtual] = useState<ContratoWithDetails | null>(null);
  // Vendedores e Parceiros não podem criar contratos, apenas visualizar os existentes
  const [etapaAtual, setEtapaAtual] = useState<'selecao' | 'confirmacao' | 'formulario' | 'visualizacao' | 'lista'>(isRestricted ? 'lista' : 'selecao');
  const [deletandoContrato, setDeletandoContrato] = useState<string | null>(null);

  // Hooks personalizados
  const { 
    propostasDisponiveis, 
    contratos, 
    loading, 
    carregarDados, 
    aceitarProposta, 
    gerarContratoPDF, 
    deletarContrato: deletarContratoService,
    reverterFinalizacaoContrato,
    atualizarStatusContrato 
  } = useContratos();

  const {
    dadosContrato,
    setDadosContrato,
    loadingAction,
    atualizarValoresKit,
    atualizarValoresIntegracao,
    validarCampos,
    obterMensagemValidacao,
    salvarContrato,
    // salvarEdicaoContrato, // Removido: não é mais possível editar dados do contrato
    preencherDadosIniciais,
    preencherDadosCompletos,
    // preencherDadosParaEdicao, // Removido
    resetarFormulario,
    calcularValorParcela,
    temMudancas
  } = useContratoForm();

  // Funções de controle do fluxo

  const selecionarProposta = (proposta: PropostaParaContrato) => {
    setPropostaSelecionada(proposta);
    preencherDadosIniciais(proposta);
    setEtapaAtual('confirmacao');
  };

  const confirmarProposta = () => {
    if (!propostaSelecionada) return;
    preencherDadosCompletos(propostaSelecionada);
    setEtapaAtual('formulario');
  };

  const salvarContratoHandler = async () => {
    if (!propostaSelecionada) return;
    try {
      const contrato = await salvarContrato(propostaSelecionada);
      setContratoAtual(contrato);
      setEtapaAtual('visualizacao');
      await carregarDados();
    } catch (error) {
      // Erro já tratado no hook
    }
  };

  // Handler de edição removido - não é mais possível alterar dados do contrato
  /*
  const salvarEdicaoContratoHandler = async () => {
    ...
  };
  */

  const gerarContratoPDFHandler = async () => {
    if (!contratoAtual) return;
    try {
      const pdfUrl = await gerarContratoPDF(contratoAtual.id);
      // Garantir que o PDF seja aberto em nova aba após a geração
      if (pdfUrl) {
        window.open(pdfUrl, '_blank');
      }
      await carregarDados();
    } catch (error) {
      // Erro já tratado no hook
    }
  };

  // Função para editar contrato removida - substituída por atualização de status
  const editarContrato = (contrato: ContratoWithDetails) => {
    // Agora apenas abre o log de debug ou redireciona, mas o botão Editar foi removido da lista
    console.log('Edição de dados desativada. Use Atualizar Status.');
  };

  const deletarContratoHandler = async (contratoId: string) => {
    // Não permitir deletar contratos finalizados
    const contrato = contratos.find(c => c.id === contratoId);
    if (contrato?.status === 'finalizado') {
      return;
    }
    try {
      setDeletandoContrato(contratoId);
      await deletarContratoService(contratoId);
    } catch (error) {
      // Erro já tratado no hook
    } finally {
      setDeletandoContrato(null);
    }
  };

  const resetarFluxo = () => {
    setPropostaSelecionada(null);
    setContratoAtual(null);
    setEtapaAtual('selecao');
    resetarFormulario();
  };

  // Função para validar campos obrigatórios
  const validarCamposObrigatorios = () => {
    const tipoCliente = propostaSelecionada?.lead_tipo || contratoAtual?.lead?.tipo;
    return validarCampos(tipoCliente);
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-4 md:p-6 space-y-4 md:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">Contratos FV</h1>
          <p className="text-sm sm:text-base text-muted-foreground">
            Gerencie contratos de clientes com propostas prontas
          </p>
        </div>
      </div>

      {/* Etapa de Seleção - Apenas para Admins */}
      {etapaAtual === 'selecao' && isAdmin && (
        <ContratoSelecao
          propostasDisponiveis={propostasDisponiveis}
          onSelecionarProposta={selecionarProposta}
        />
      )}


      {/* Etapa de Revisão */}
      {etapaAtual === 'confirmacao' && propostaSelecionada && (
        <ContratoConfirmacao
          propostaSelecionada={propostaSelecionada}
          onConfirmar={confirmarProposta}
          onCancelar={resetarFluxo}
        />
      )}

      {/* Etapa de Formulário */}
      {etapaAtual === 'formulario' && propostaSelecionada && (
        <Card>
          <CardHeader>
            <CardTitle>Informações do Contrato</CardTitle>
            <CardDescription>
              Preencha as informações necessárias para o contrato
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <ContratoFormulario
              modo="formulario"
              propostaSelecionada={propostaSelecionada}
              contratoEditando={null}
              dadosContrato={dadosContrato}
              setDadosContrato={setDadosContrato}
              loadingAction={loadingAction}
              onSalvar={salvarContratoHandler}
              onCancelar={resetarFluxo}
              validarCamposObrigatorios={validarCamposObrigatorios}
              obterMensagemValidacao={obterMensagemValidacao}
              calcularValorParcela={calcularValorParcela}
              atualizarValoresKit={atualizarValoresKit}
              atualizarValoresIntegracao={atualizarValoresIntegracao}
              temMudancas={temMudancas}
            />
          </CardContent>
        </Card>
      )}

      {/* Etapa de Visualização */}
      {etapaAtual === 'visualizacao' && contratoAtual && (
        <ContratoVisualizacao
          contratoAtual={contratoAtual}
          loadingAction={loadingAction}
          onGerarPDF={gerarContratoPDFHandler}
          onCriarNovo={resetarFluxo}
        />
      )}

      {/* Lista de Contratos Existentes */}
      {(etapaAtual === 'selecao' || etapaAtual === 'lista') && (
        <ContratoLista
          contratos={contratos}
          deletandoContrato={deletandoContrato}
          onEditar={editarContrato}
          onDeletar={deletarContratoHandler}
          onReverterFinalizacao={reverterFinalizacaoContrato}
          onAtualizarStatus={atualizarStatusContrato}
          onGerarPDF={async (contratoId: string) => {
            try {
              const pdfUrl = await gerarContratoPDF(contratoId);
              // Garantir que o PDF seja aberto em nova aba após a geração
              if (pdfUrl) {
                window.open(pdfUrl, '_blank');
              }
            } catch (error) {
              // Erro já tratado no hook
            }
          }}
        />
      )}

      {/* Botão Voltar na parte inferior esquerda - Apenas para Admins */}
      {etapaAtual !== 'selecao' && etapaAtual !== 'lista' && isAdmin && (
        <div className="flex justify-start pt-4 md:pt-6">
          <Button
            onClick={resetarFluxo}
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm">Voltar à Lista</span>
          </Button>
        </div>
      )}
    </div>
  );
}
